import { THEMES, type ThemeId } from "@justone/shared";

/* 화면 테마: <html data-theme data-scheme>. index.html의 스크립트가 처음 그리기 전에 같은 일을 한다 (깜빡임 없게) */

const KEY = "justone:theme";
const media = () => window.matchMedia("(prefers-color-scheme: dark)");

/** 견본·주소창 색 (app.css의 테마 색과 맞춘다) */
export const THEME_LOOK: Record<ThemeId, { side: string; sideText: string; paper: string; surface: string; accent: string; text: string; muted: string }> = {
  system: { side: "#17322c", sideText: "#d6e5df", paper: "#fbfcfa", surface: "#ffffff", accent: "#2e7d63", text: "#1b2420", muted: "#cbd5d0" },
  "pine-light": { side: "#17322c", sideText: "#d6e5df", paper: "#fbfcfa", surface: "#ffffff", accent: "#2e7d63", text: "#1b2420", muted: "#cbd5d0" },
  "pine-dark": { side: "#0f211d", sideText: "#d6e5df", paper: "#141a18", surface: "#1a211e", accent: "#5cc19b", text: "#e4ebe7", muted: "#3a4742" },
  nord: { side: "#272c36", sideText: "#d8dee9", paper: "#2e3440", surface: "#3b4252", accent: "#88c0d0", text: "#eceff4", muted: "#4c566a" },
  "nord-deep": { side: "#1b1f26", sideText: "#d8dee9", paper: "#242933", surface: "#2b303b", accent: "#81a1c1", text: "#eceff4", muted: "#434c5e" },
  "nord-aurora": { side: "#272c36", sideText: "#d8dee9", paper: "#2e3440", surface: "#3b4252", accent: "#a3be8c", text: "#eceff4", muted: "#4c566a" },
  "nord-light": { side: "#e5e9f0", sideText: "#3b4252", paper: "#eceff4", surface: "#f8f9fb", accent: "#5e81ac", text: "#2e3440", muted: "#c3ccda" },
  "nord-frost": { side: "#2e3440", sideText: "#d8dee9", paper: "#eceff4", surface: "#ffffff", accent: "#4c8a9c", text: "#2e3440", muted: "#c3ccda" },
  terra: { side: "#3b2a21", sideText: "#ead9c6", paper: "#f6f0e8", surface: "#fffcf7", accent: "#b4532a", text: "#2f241d", muted: "#d3c2ae" },
  "terra-dark": { side: "#1a130f", sideText: "#e3d2bf", paper: "#211914", surface: "#2a201a", accent: "#d47a4e", text: "#f0e4d6", muted: "#4d3c30" },
};

const isTheme = (v: string | null): v is ThemeId => THEMES.some((t) => t.id === v);

export function storedTheme(): ThemeId {
  try {
    const v = localStorage.getItem(KEY);
    return isTheme(v) ? v : "system";
  } catch {
    return "system";
  }
}

let unlisten: (() => void) | null = null;

/** 테마를 화면에 입힌다 (기기 저장도). 소나무(기본)면 기기의 밝음·어두움 설정이 바뀔 때 따라간다 */
export function applyTheme(id: ThemeId) {
  const root = document.documentElement;
  const theme = THEMES.find((t) => t.id === id) ?? THEMES[0];
  unlisten?.();
  unlisten = null;
  const set = () => {
    const dark = theme.dark ?? media().matches;
    root.dataset.scheme = dark ? "dark" : "light";
    const look = THEME_LOOK[theme.id];
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme.dark === null && dark ? THEME_LOOK["pine-dark"].side : look.side);
  };
  if (theme.id === "system" || theme.id === "pine-light" || theme.id === "pine-dark") delete root.dataset.theme;
  else root.dataset.theme = theme.id;
  set();
  if (theme.dark === null) {
    const m = media();
    m.addEventListener("change", set);
    unlisten = () => m.removeEventListener("change", set);
  }
  try {
    localStorage.setItem(KEY, theme.id);
  } catch {
    // 저장소를 못 쓰면 이번 화면에서만
  }
}
